import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { useSectionAccess } from '../hooks/useSectionAccess';
import {
  getEmployees, generateEmployeeLetter, getIssuedLetters, getLetterStats,
  getFnfReleasedEmployees, getHRLetterTypes, generateHRLetter, redownloadHRLetter, getIssuedHRLetters, getHRLetterStats
} from '../api';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  FileText, Download, Plus, Users, Award, Home,
  Calendar, RefreshCw, Search, FileCheck, AlertTriangle,
  ShieldAlert, UserX, Clock, Ban, Briefcase, Mail, Package
} from 'lucide-react';
import { toast } from 'sonner';

/* ── Existing Employee Letters Tab ── */
const EmployeeLettersTab = ({ user, employees, fnfReleasedEmployees, fetchOriginalData }) => {
  const [issuedLetters, setIssuedLetters] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({ employee_id: '', letter_type: 'domicile', last_working_date: '', reason_for_leaving: 'resignation', additional_notes: '' });
  const [filterLetterType, setFilterLetterType] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [lettersRes, statsRes] = await Promise.all([getIssuedLetters({}), getLetterStats()]);
      setIssuedLetters(lettersRes.data || []);
      setStats(statsRes.data);
    } catch { toast.error('Failed to load letters data'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const getEligibleEmployees = () => {
    if (formData.letter_type === 'domicile') return employees;
    return fnfReleasedEmployees.map(fnf => ({ id: fnf.employee_id, name: fnf.employee_name, employee_id: fnf.employee_code, last_working_date: fnf.last_working_date }));
  };

  const handleGenerate = async () => {
    if (!formData.employee_id) { toast.error('Please select an employee'); return; }
    try {
      setGenerating(true);
      const response = await generateEmployeeLetter(formData);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const emp = employees.find(e => e.id === formData.employee_id);
      a.download = `${formData.letter_type}_Letter_${emp?.employee_id || 'EMP'}.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Letter generated successfully');
      setDialogOpen(false);
      setFormData({ employee_id: '', letter_type: 'domicile', last_working_date: '', reason_for_leaving: 'resignation', additional_notes: '' });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to generate letter'); }
    finally { setGenerating(false); }
  };

  const filteredLetters = issuedLetters.filter(l => {
    const matchType = filterLetterType === 'all' || l.letter_type === filterLetterType;
    const matchSearch = !searchQuery || l.employee_name?.toLowerCase().includes(searchQuery.toLowerCase()) || l.employee_code?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchType && matchSearch;
  });

  const badgeColors = { relieving: 'bg-blue-100 text-blue-700', experience: 'bg-emerald-100 text-emerald-700', domicile: 'bg-purple-100 text-purple-700' };
  const icons = { relieving: <FileCheck className="h-4 w-4 text-blue-600" />, experience: <Award className="h-4 w-4 text-emerald-600" />, domicile: <Home className="h-4 w-4 text-purple-600" /> };

  return (
    <div className="space-y-5">
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[
            { label: 'Relieving', value: stats.relieving_letters, icon: <FileCheck className="h-5 w-5 text-blue-600" />, bg: 'bg-blue-100' },
            { label: 'Experience', value: stats.experience_letters, icon: <Award className="h-5 w-5 text-emerald-600" />, bg: 'bg-emerald-100' },
            { label: 'Domicile', value: stats.domicile_letters, icon: <Home className="h-5 w-5 text-purple-600" />, bg: 'bg-purple-100' },
            { label: 'Last 30 Days', value: stats.recent_30_days, icon: <Calendar className="h-5 w-5 text-amber-600" />, bg: 'bg-amber-100' },
          ].map(s => (
            <Card key={s.label}><CardContent className="p-4"><div className="flex items-center gap-3"><div className={`w-10 h-10 ${s.bg} rounded-lg flex items-center justify-center`}>{s.icon}</div><div><p className="text-sm text-slate-500">{s.label}</p><p className="text-2xl font-bold text-slate-900">{s.value}</p></div></div></CardContent></Card>
          ))}
        </div>
      )}

      {/* Filters + Button */}
      <Card><CardContent className="p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[200px]">
            <Label className="text-xs text-slate-500 mb-1 block">Search</Label>
            <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" /><Input placeholder="Search by name or code..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9" data-testid="emp-letter-search" /></div>
          </div>
          <div className="w-48">
            <Label className="text-xs text-slate-500 mb-1 block">Letter Type</Label>
            <Select value={filterLetterType} onValueChange={setFilterLetterType}><SelectTrigger data-testid="emp-letter-type-filter"><SelectValue placeholder="All Types" /></SelectTrigger><SelectContent><SelectItem value="all">All Types</SelectItem><SelectItem value="relieving">Relieving</SelectItem><SelectItem value="experience">Experience</SelectItem><SelectItem value="domicile">Domicile</SelectItem></SelectContent></Select>
          </div>
          <Button variant="outline" onClick={() => { fetchData(); fetchOriginalData(); }} data-testid="emp-letter-refresh"><RefreshCw className="h-4 w-4 mr-2" />Refresh</Button>
          <Button onClick={() => setDialogOpen(true)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="generate-emp-letter-btn"><Plus className="h-4 w-4 mr-2" />Generate Letter</Button>
        </div>
      </CardContent></Card>

      {/* Table */}
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-lg flex items-center gap-2"><FileText className="h-5 w-5 text-indigo-600" />Issued Letters ({filteredLetters.length})</CardTitle></CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" /></div>
          ) : filteredLetters.length === 0 ? (
            <div className="text-center py-12 text-slate-500"><FileText className="h-12 w-12 mx-auto text-slate-300 mb-3" /><p>No letters issued yet</p></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50"><tr>{['Letter Type','Employee','Letter No.','Last Working Date','Issued By','Issued On'].map(h => <th key={h} className="text-left py-3 px-4 text-sm font-medium text-slate-600">{h}</th>)}</tr></thead>
                <tbody>
                  {filteredLetters.map((l, i) => (
                    <tr key={l.id || i} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-3 px-4"><div className="flex items-center gap-2">{icons[l.letter_type]}<Badge className={`${badgeColors[l.letter_type] || 'bg-slate-100 text-slate-700'} capitalize`}>{l.letter_type}</Badge></div></td>
                      <td className="py-3 px-4"><p className="font-medium text-slate-900">{l.employee_name}</p><p className="text-xs text-slate-500">{l.employee_code}</p></td>
                      <td className="py-3 px-4"><p className="text-sm text-slate-600 font-mono">{l.letter_number}</p></td>
                      <td className="py-3 px-4 text-sm text-slate-600">{l.last_working_date ? new Date(l.last_working_date).toLocaleDateString() : '-'}</td>
                      <td className="py-3 px-4 text-sm text-slate-600">{l.issued_by_name}</td>
                      <td className="py-3 px-4 text-sm text-slate-600">{new Date(l.issued_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="text-indigo-600 flex items-center gap-2"><FileText className="h-5 w-5" />Generate Employee Letter</DialogTitle><DialogDescription>Select employee and letter type to generate</DialogDescription></DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Letter Type *</Label>
              <Select value={formData.letter_type} onValueChange={v => setFormData({ ...formData, letter_type: v, employee_id: '' })}>
                <SelectTrigger data-testid="emp-letter-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="domicile">Domicile Letter (All Employees)</SelectItem>
                  <SelectItem value="relieving">Relieving Letter (FnF Released Only)</SelectItem>
                  <SelectItem value="experience">Experience Letter (FnF Released Only)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {formData.letter_type !== 'domicile' && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2"><AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" /><div className="text-sm text-amber-700"><p className="font-medium">FnF Settlement Required</p><p>Only employees with processed Full & Final settlement are eligible.</p></div></div>
            )}
            <div>
              <Label>Employee * {formData.letter_type !== 'domicile' && `(${getEligibleEmployees().length} eligible)`}</Label>
              <Select value={formData.employee_id} onValueChange={v => { const emp = getEligibleEmployees().find(e => e.id === v); setFormData({ ...formData, employee_id: v, last_working_date: emp?.last_working_date || formData.last_working_date }); }}>
                <SelectTrigger data-testid="emp-letter-employee-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>{getEligibleEmployees().length === 0 ? <div className="p-2 text-center text-slate-500 text-sm">No eligible employees</div> : getEligibleEmployees().map(e => <SelectItem key={e.id} value={e.id}>{e.name || e.employee_id} - {e.employee_id}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {formData.letter_type !== 'domicile' && <div><Label>Last Working Date</Label><Input type="date" value={formData.last_working_date} onChange={e => setFormData({ ...formData, last_working_date: e.target.value })} /></div>}
            {formData.letter_type === 'relieving' && <div><Label>Reason for Leaving</Label><Select value={formData.reason_for_leaving} onValueChange={v => setFormData({ ...formData, reason_for_leaving: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="resignation">Resignation</SelectItem><SelectItem value="retirement">Retirement</SelectItem><SelectItem value="end_of_contract">End of Contract</SelectItem><SelectItem value="mutual_separation">Mutual Separation</SelectItem></SelectContent></Select></div>}
            <div><Label>Additional Notes (Optional)</Label><Textarea value={formData.additional_notes} onChange={e => setFormData({ ...formData, additional_notes: e.target.value })} placeholder="Any additional notes..." rows={2} /></div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleGenerate} disabled={generating} className="bg-indigo-600 hover:bg-indigo-700" data-testid="emp-letter-generate-submit">{generating ? 'Generating...' : <><Download className="h-4 w-4 mr-2" />Generate & Download</>}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/* ── HR Letters Tab (7 new types) ── */
const HR_LETTER_CONFIG = {
  short_notice: { label: 'Short-Notice Recovery', icon: Clock, color: 'text-orange-600', bg: 'bg-orange-100', badgeCls: 'bg-orange-100 text-orange-700',
    fields: [
      { key: 'notice_period_days', label: 'Notice Period (Days)', type: 'number' },
      { key: 'days_served', label: 'Days Served', type: 'number' },
      { key: 'recovery_amount', label: 'Recovery Amount (INR)', type: 'number' },
      { key: 'last_working_date', label: 'Last Working Date', type: 'date' },
    ]},
  warning: { label: 'Warning Letter', icon: ShieldAlert, color: 'text-red-600', bg: 'bg-red-100', badgeCls: 'bg-red-100 text-red-700',
    fields: [
      { key: 'incident_date', label: 'Incident Date', type: 'date' },
      { key: 'incident_description', label: 'Incident Description', type: 'textarea' },
      { key: 'previous_warnings', label: 'Previous Warnings Count', type: 'number' },
      { key: 'expected_behavior', label: 'Expected Behavior', type: 'textarea' },
      { key: 'consequences', label: 'Consequences', type: 'textarea' },
    ]},
  ncns: { label: 'NCNS (No Call No Show)', icon: Ban, color: 'text-amber-600', bg: 'bg-amber-100', badgeCls: 'bg-amber-100 text-amber-700',
    fields: [
      { key: 'absence_dates', label: 'Absence Date(s)', type: 'text', placeholder: 'e.g. 10 Jan 2026, 11 Jan 2026' },
    ]},
  absconding: { label: 'Absconding Letter', icon: UserX, color: 'text-rose-600', bg: 'bg-rose-100', badgeCls: 'bg-rose-100 text-rose-700',
    fields: [
      { key: 'last_present_date', label: 'Last Present Date', type: 'date' },
      { key: 'days_absent', label: 'Days Absent', type: 'number' },
    ]},
  termination: { label: 'Termination Letter', icon: Briefcase, color: 'text-slate-700', bg: 'bg-slate-200', badgeCls: 'bg-slate-200 text-slate-700',
    fields: [
      { key: 'termination_date', label: 'Termination Date', type: 'date' },
      { key: 'termination_reason', label: 'Reason for Termination', type: 'textarea' },
      { key: 'fnf_details', label: 'F&F Settlement Details', type: 'textarea' },
    ]},
  resignation_ack: { label: 'Resignation Acknowledgment', icon: Mail, color: 'text-sky-600', bg: 'bg-sky-100', badgeCls: 'bg-sky-100 text-sky-700',
    fields: [
      { key: 'resignation_date', label: 'Resignation Date', type: 'date' },
      { key: 'last_working_date', label: 'Last Working Date', type: 'date' },
      { key: 'handover_instructions', label: 'Handover Instructions', type: 'textarea' },
    ]},
  asset_recovery: { label: 'Asset Recovery Notice', icon: Package, color: 'text-violet-600', bg: 'bg-violet-100', badgeCls: 'bg-violet-100 text-violet-700',
    fields: [
      { key: 'pending_assets', label: 'Pending Assets (one per line)', type: 'textarea', placeholder: 'Laptop - Dell Latitude 5520\nID Card\nAccess Card' },
      { key: 'return_deadline', label: 'Return Deadline', type: 'date' },
    ]},
};

const HRLettersTab = ({ employees }) => {
  const [issuedLetters, setIssuedLetters] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedType, setSelectedType] = useState('warning');
  const [filterType, setFilterType] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({});

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [lettersRes, statsRes] = await Promise.all([getIssuedHRLetters({}), getHRLetterStats()]);
      setIssuedLetters(lettersRes.data || []);
      setStats(statsRes.data);
    } catch { toast.error('Failed to load HR letters'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openDialog = (type) => {
    setSelectedType(type);
    setFormData({ employee_name: '', employee_code: '', designation: '', department: '', date_of_joining: '', employee_id: '', additional_notes: '' });
    setDialogOpen(true);
  };

  const handleAutoFill = (empId) => {
    const emp = employees.find(e => e.id === empId);
    if (!emp) return;
    setFormData(prev => ({
      ...prev,
      employee_id: empId,
      employee_name: emp.name || '',
      employee_code: emp.employee_id || '',
      designation: emp.designation || '',
      department: emp.department || '',
      date_of_joining: emp.date_of_joining || '',
    }));
  };

  const handleGenerate = async () => {
    if (!formData.employee_name) { toast.error('Employee name is required'); return; }
    try {
      setGenerating(true);
      const payload = { letter_type: selectedType, ...formData };
      // Convert number fields
      ['notice_period_days', 'days_served', 'recovery_amount', 'previous_warnings', 'days_absent'].forEach(k => {
        if (payload[k] !== undefined && payload[k] !== '') payload[k] = Number(payload[k]);
        else if (payload[k] === '') delete payload[k];
      });
      const response = await generateHRLetter(payload);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${HR_LETTER_CONFIG[selectedType].label.replace(/\s+/g, '_')}_${formData.employee_code || formData.employee_name.replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success(`${HR_LETTER_CONFIG[selectedType].label} generated successfully`);
      setDialogOpen(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to generate letter'); }
    finally { setGenerating(false); }
  };

  const handleRedownload = async (letter) => {
    try {
      toast.info('Downloading...');
      const response = await redownloadHRLetter(letter.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(letter.letter_label || letter.letter_type).replace(/\s+/g, '_')}_${letter.employee_code || letter.employee_name.replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Letter downloaded');
    } catch (err) { toast.error(err.response?.data?.detail || 'Re-download failed'); }
  };

  const filteredLetters = issuedLetters.filter(l => {
    const matchType = filterType === 'all' || l.letter_type === filterType;
    const matchSearch = !searchQuery || l.employee_name?.toLowerCase().includes(searchQuery.toLowerCase()) || l.employee_code?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchType && matchSearch;
  });

  const cfg = HR_LETTER_CONFIG[selectedType];

  return (
    <div className="space-y-5">
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card><CardContent className="p-4"><div className="flex items-center gap-3"><div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center"><FileText className="h-5 w-5 text-indigo-600" /></div><div><p className="text-sm text-slate-500">Total HR Letters</p><p className="text-2xl font-bold text-slate-900">{stats.total}</p></div></div></CardContent></Card>
          {Object.entries(stats.by_type || {}).slice(0, 3).map(([type, count]) => {
            const c = HR_LETTER_CONFIG[type];
            if (!c) return null;
            const Icon = c.icon;
            return <Card key={type}><CardContent className="p-4"><div className="flex items-center gap-3"><div className={`w-10 h-10 ${c.bg} rounded-lg flex items-center justify-center`}><Icon className={`h-5 w-5 ${c.color}`} /></div><div><p className="text-sm text-slate-500">{c.label}</p><p className="text-2xl font-bold text-slate-900">{count}</p></div></div></CardContent></Card>;
          })}
        </div>
      )}

      {/* Letter Type Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {Object.entries(HR_LETTER_CONFIG).map(([type, c]) => {
          const Icon = c.icon;
          return (
            <Card key={type} className="cursor-pointer hover:shadow-md transition-shadow border-2 hover:border-indigo-300" onClick={() => openDialog(type)} data-testid={`hr-letter-card-${type}`}>
              <CardContent className="p-4 flex items-center gap-3">
                <div className={`w-10 h-10 ${c.bg} rounded-lg flex items-center justify-center flex-shrink-0`}><Icon className={`h-5 w-5 ${c.color}`} /></div>
                <div><p className="font-medium text-slate-800 text-sm">{c.label}</p><p className="text-xs text-slate-500">Click to generate</p></div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Filters */}
      <Card><CardContent className="p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[200px]">
            <Label className="text-xs text-slate-500 mb-1 block">Search</Label>
            <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" /><Input placeholder="Search by name or code..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9" data-testid="hr-letter-search" /></div>
          </div>
          <div className="w-56">
            <Label className="text-xs text-slate-500 mb-1 block">Letter Type</Label>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger data-testid="hr-letter-type-filter"><SelectValue placeholder="All Types" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {Object.entries(HR_LETTER_CONFIG).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" onClick={fetchData} data-testid="hr-letter-refresh"><RefreshCw className="h-4 w-4 mr-2" />Refresh</Button>
        </div>
      </CardContent></Card>

      {/* Issued Table */}
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-lg flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-indigo-600" />Issued HR Letters ({filteredLetters.length})</CardTitle></CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" /></div>
          ) : filteredLetters.length === 0 ? (
            <div className="text-center py-12 text-slate-500"><ShieldAlert className="h-12 w-12 mx-auto text-slate-300 mb-3" /><p>No HR letters issued yet</p><p className="text-sm">Click any letter card above to generate one</p></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50"><tr>{['Type','Employee','Letter No.','Issued By','Issued On',''].map(h => <th key={h} className="text-left py-3 px-4 text-sm font-medium text-slate-600">{h}</th>)}</tr></thead>
                <tbody>
                  {filteredLetters.map((l, i) => {
                    const c = HR_LETTER_CONFIG[l.letter_type];
                    return (
                      <tr key={l.id || i} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-3 px-4"><Badge className={c?.badgeCls || 'bg-slate-100 text-slate-700'}>{l.letter_label || l.letter_type}</Badge></td>
                        <td className="py-3 px-4"><p className="font-medium text-slate-900">{l.employee_name}</p><p className="text-xs text-slate-500">{l.employee_code}</p></td>
                        <td className="py-3 px-4 text-sm text-slate-600 font-mono">{l.letter_number}</td>
                        <td className="py-3 px-4 text-sm text-slate-600">{l.issued_by_name}</td>
                        <td className="py-3 px-4 text-sm text-slate-600">{new Date(l.issued_at).toLocaleDateString()}</td>
                        <td className="py-3 px-4">
                          <Button variant="ghost" size="sm" onClick={() => handleRedownload(l)} className="text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50" data-testid={`hr-letter-redownload-${l.id}`}>
                            <Download className="h-4 w-4 mr-1" />Re-download
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Generate Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-indigo-600 flex items-center gap-2">
              {cfg && <cfg.icon className="h-5 w-5" />}
              {cfg?.label}
            </DialogTitle>
            <DialogDescription>Fill in details or auto-fill from employee records</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Auto-fill */}
            <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
              <Label className="text-sm font-medium text-indigo-700 mb-2 block">Auto-fill from Employee (Optional)</Label>
              <Select onValueChange={handleAutoFill}>
                <SelectTrigger data-testid="hr-letter-autofill-select"><SelectValue placeholder="Select to auto-fill..." /></SelectTrigger>
                <SelectContent>{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name} - {e.employee_id}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            {/* Common fields */}
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Employee Name *</Label><Input value={formData.employee_name || ''} onChange={e => setFormData({ ...formData, employee_name: e.target.value })} data-testid="hr-letter-emp-name" /></div>
              <div><Label>Employee Code</Label><Input value={formData.employee_code || ''} onChange={e => setFormData({ ...formData, employee_code: e.target.value })} data-testid="hr-letter-emp-code" /></div>
              <div><Label>Designation</Label><Input value={formData.designation || ''} onChange={e => setFormData({ ...formData, designation: e.target.value })} /></div>
              <div><Label>Department</Label><Input value={formData.department || ''} onChange={e => setFormData({ ...formData, department: e.target.value })} /></div>
            </div>

            {/* Type-specific fields */}
            {cfg?.fields.map(f => (
              <div key={f.key}>
                <Label>{f.label}</Label>
                {f.type === 'textarea' ? (
                  <Textarea value={formData[f.key] || ''} onChange={e => setFormData({ ...formData, [f.key]: e.target.value })} placeholder={f.placeholder || ''} rows={3} data-testid={`hr-letter-field-${f.key}`} />
                ) : (
                  <Input type={f.type} value={formData[f.key] || ''} onChange={e => setFormData({ ...formData, [f.key]: e.target.value })} placeholder={f.placeholder || ''} data-testid={`hr-letter-field-${f.key}`} />
                )}
              </div>
            ))}

            <div><Label>Additional Notes (Optional)</Label><Textarea value={formData.additional_notes || ''} onChange={e => setFormData({ ...formData, additional_notes: e.target.value })} placeholder="Any additional notes..." rows={2} /></div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleGenerate} disabled={generating} className="bg-indigo-600 hover:bg-indigo-700" data-testid="hr-letter-generate-submit">{generating ? 'Generating...' : <><Download className="h-4 w-4 mr-2" />Generate & Download</>}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/* ── Main Page ── */
const EmployeeLetters = ({ user, setUser }) => {
  const [employees, setEmployees] = useState([]);
  const [fnfReleasedEmployees, setFnfReleasedEmployees] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchBaseData = useCallback(async () => {
    try {
      setLoading(true);
      const [empRes, fnfRes] = await Promise.all([getEmployees(), getFnfReleasedEmployees()]);
      setEmployees(empRes.data || []);
      setFnfReleasedEmployees(fnfRes.data || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchBaseData(); }, [fetchBaseData]);

  const hasAccess = useSectionAccess('/employee-letters');
  if (hasAccess === false) {
    return (
      <Layout user={user} setUser={setUser}>
        <div className="flex flex-col items-center justify-center h-96">
          <FileText className="h-16 w-16 text-red-400 mb-4" />
          <h2 className="text-xl font-semibold text-slate-700">Access Denied</h2>
          <p className="text-slate-500 mt-2">You don't have permission to view this page.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} setUser={setUser}>
      <div className="space-y-6" data-testid="employee-letters">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Employee Letters</h1>
          <p className="text-gray-500 mt-1">Generate employee & HR letters with company letterhead</p>
        </div>

        <Tabs defaultValue="employee" className="w-full">
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="employee" data-testid="tab-employee-letters">
              <FileCheck className="h-4 w-4 mr-2" />Employee Letters
            </TabsTrigger>
            <TabsTrigger value="hr" data-testid="tab-hr-letters">
              <ShieldAlert className="h-4 w-4 mr-2" />HR Letters
            </TabsTrigger>
          </TabsList>

          <TabsContent value="employee" className="mt-4">
            <EmployeeLettersTab user={user} employees={employees} fnfReleasedEmployees={fnfReleasedEmployees} fetchOriginalData={fetchBaseData} />
          </TabsContent>

          <TabsContent value="hr" className="mt-4">
            {loading ? (
              <div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" /></div>
            ) : (
              <HRLettersTab employees={employees} />
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default EmployeeLetters;
